import { ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isBefore, startOfDay } from 'date-fns';
import { cn } from '@c2fo/liquidity';
import { isDemoModeActive } from '@/demo/demoMode';
import { PageBottomPortalProvider } from '@/enterprise/components/PageBottomPortal/PageBottomPortalContext';
import { useShowPrivacyPolicyBanner } from '@/enterprise/data/useTerms';
import HolidayDelayBanner from '@/enterprise/features/homepage/HolidayDelayBanner';
import PrivacyPolicyBanner from '@/enterprise/features/homepage/PrivacyPolicyBanner';
import useFeature from '@/lib/features';
import { isImpersonationSession } from '@/utils/token';
import { useReadOnlyUser } from '@/utils/useRestrictions';
import DemoBanner from '../../demo/components/DemoBanner';
import useCurrencies from '../data/useCurrencies';
import Footer from './Footer';
import { ImpersonationBanner } from './ImpersonationBanner';
import ReadOnlyBanner from './ReadOnlyBanner';
import SideNavigation from './SideNavigation/Navigation';
import { useNavigationContext } from './SideNavigation/NavigationContext';
import SideNavigationMobile from './SideNavigation/NavigationMobile';
import SideNavigationTop from './SideNavigation/NavigationTop';

const AppLayout = ({ children }: { children: ReactNode }) => {
  const readOnlyUser = useReadOnlyUser();
  const isImpersonation = isImpersonationSession();
  const { showPrivacyPolicyBanner } = useShowPrivacyPolicyBanner();
  const pageBottomPortalRef = useRef<HTMLDivElement>(null);
  const { isNavCollapsed } = useNavigationContext();
  const [enablePrivacyPolicyBanner] = useFeature('enterprise-ui_enablePrivacyPolicyBanner');
  const currencies = useCurrencies();
  const { i18n } = useTranslation();
  const hasEligibleCurrency = currencies.some((currency) => ['USD', 'GBP', 'EUR'].includes(currency));
  const isEnglish = i18n.language.startsWith('en');
  // startOfDay(new Date()) is today at local midnight. new Date(2026, 6, 4) is July 4 at local midnight, month is 0-indexed.
  // Window covers today through Independence Day observed (Jul 3).
  const isBeforeJul4th = isBefore(startOfDay(new Date()), startOfDay(new Date(2026, 6, 4)));
  const [holidayDelayBannerDismissed, setHolidayDelayBannerDismissed] = useState(false);
  const showHolidayDelayBanner = !holidayDelayBannerDismissed && hasEligibleCurrency && isEnglish && isBeforeJul4th;

  const showPPBanner = enablePrivacyPolicyBanner && showPrivacyPolicyBanner && !isImpersonation && !readOnlyUser;
  const isDemoActive = isDemoModeActive();

  // calculate total banner height, each banner is h-10 which is 40px
  const bannerHeight =
    [isImpersonation, readOnlyUser, showPPBanner, showHolidayDelayBanner, isDemoActive].filter(Boolean).length * 40;

  return (
    <div className="h-full w-full bg-white">
      <div className="flex">
        <SideNavigation className="fixed left-0 top-0 z-10 hidden h-screen md:block" />
        <div
          className={cn('min-h-screen w-full bg-canvas transition-[padding-left] duration-200 ease-in-out md:pl-64', {
            'md:pl-14': isNavCollapsed,
          })}
        >
          <PageBottomPortalProvider containerRef={pageBottomPortalRef}>
            <div className="flex min-h-screen flex-col">
              <div className="sticky top-0 z-10">
                {isDemoActive && <DemoBanner />}
                {isImpersonation && <ImpersonationBanner />}
                {readOnlyUser && <ReadOnlyBanner isImpersonation={isImpersonation} />}
                {showPPBanner && <PrivacyPolicyBanner />}
                {showHolidayDelayBanner && (
                  <HolidayDelayBanner onDismiss={() => setHolidayDelayBannerDismissed(true)} />
                )}
                <SideNavigationMobile />
              </div>
              <div className="sticky z-[9]" style={{ top: `${bannerHeight}px` }}>
                <SideNavigationTop />
              </div>
              <div className="grow">
                <div className="mx-auto max-w-screen-xl">
                  <div className="px-4 py-10 md:px-6">{children}</div>
                  <Footer />
                </div>
              </div>
              <div ref={pageBottomPortalRef} className="pointer-events-none sticky bottom-0 z-[9]" />
            </div>
          </PageBottomPortalProvider>
        </div>
      </div>
    </div>
  );
};

export default AppLayout;
